import { useCallback, useEffect, useRef, useState } from "react";
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  Library,
  Loader2,
  Plus,
  Send,
  Settings2,
  X,
} from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { Textarea } from "@/components/ui/textarea";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { chatSendButtonClassName } from "@/components/chat/chat-controls.js";
import {
  CHAT_COMPOSER_MAX_HEIGHT_PX,
  CHAT_COMPOSER_MIN_HEIGHT_PX,
  resizeComposerTextarea,
} from "@/components/chat/resizeComposerTextarea";
import { cn } from "@/lib/utils";
import { RagBadge } from "./RagBadge.js";
import {
  KmComposerPlusMenu,
  type KmComposerPlusMenuView,
} from "./KmComposerPlusMenu.js";
import {
  attachedDocStatusLabel,
  canRemoveAttachedDoc,
  isAttachedDocProcessing,
} from "../lib/attached-doc-utils.js";
import type { DocumentStatus } from "../types.js";

interface LibraryScopeOption {
  id: string;
  name: string;
  selected: boolean;
}

export interface AttachedDoc {
  id: string;
  name: string;
  status: DocumentStatus;
}

interface ChatComposerProps {
  placeholder?: string;
  scopeLabel?: string;
  scopeVariant?: "library" | "workspace";
  libraryScope?: LibraryScopeOption[];
  /** @deprecated Prefer opening Libraries via the + menu; kept for callers that still pass it. */
  onManage?: () => void;
  manageLabel?: string;
  attachedDocs?: AttachedDoc[];
  onRemoveDoc?: (docId: string) => void;
  onAttachDocs?: () => void;
  showLibraryScopeBar?: boolean;
  onLibraryIdsChange?: (ids: string[]) => void;
  onSubmit?: (message: string) => Promise<void>;
  disabled?: boolean;
  agenticAvailable?: boolean;
  agenticEnabled?: boolean;
  onAgenticChange?: (enabled: boolean) => void;
  className?: string;
}

function buildContextSummary(
  selectedLibraries: LibraryScopeOption[],
  attachedDocs: AttachedDoc[],
  hasScopeBar: boolean,
): string {
  const attachedCount = attachedDocs.length;
  const attachedPart =
    attachedCount === 1 ? "1 attached" : `${attachedCount} attached`;

  if (!hasScopeBar) {
    return attachedPart;
  }

  if (selectedLibraries.length === 0 && attachedCount === 0) {
    return "No libraries selected";
  }

  if (selectedLibraries.length === 1) {
    return attachedCount > 0
      ? `${selectedLibraries[0].name} · ${attachedPart}`
      : selectedLibraries[0].name;
  }

  if (attachedCount > 0) {
    return `${selectedLibraries.length} libraries · ${attachedPart}`;
  }

  return `${selectedLibraries.length} libraries`;
}

export function ChatComposer({
  placeholder = "Ask about your documents…",
  scopeLabel,
  scopeVariant = "library",
  libraryScope,
  onManage: _onManage,
  manageLabel = "Manage scope",
  attachedDocs = [],
  onRemoveDoc,
  onAttachDocs,
  showLibraryScopeBar = false,
  onLibraryIdsChange,
  onSubmit,
  disabled = false,
  agenticAvailable = false,
  agenticEnabled = true,
  onAgenticChange,
  className,
}: ChatComposerProps) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [plusMenuInitialView, setPlusMenuInitialView] =
    useState<KmComposerPlusMenuView>("root");
  const [isContextExpanded, setIsContextExpanded] = useLocalStorage<boolean>(
    "chat-composer-context-expanded",
    false,
  );
  const [textareaHeight, setTextareaHeight] = useState(CHAT_COMPOSER_MIN_HEIGHT_PX);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevAttachedCountRef = useRef(attachedDocs.length);
  const isMultiline = textareaHeight > CHAT_COMPOSER_MIN_HEIGHT_PX;

  const syncTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const nextHeight = resizeComposerTextarea(
      textarea,
      CHAT_COMPOSER_MIN_HEIGHT_PX,
      CHAT_COMPOSER_MAX_HEIGHT_PX,
    );
    setTextareaHeight(nextHeight);
  }, []);

  useEffect(() => {
    if (attachedDocs.length > prevAttachedCountRef.current) {
      setIsContextExpanded(true);
    }
    prevAttachedCountRef.current = attachedDocs.length;
  }, [attachedDocs.length, setIsContextExpanded]);

  useEffect(() => {
    syncTextareaHeight();
    const id = requestAnimationFrame(() => syncTextareaHeight());
    return () => cancelAnimationFrame(id);
  }, [message, placeholder, syncTextareaHeight]);

  async function handleSubmit() {
    const trimmed = message.trim();
    if (!trimmed || !onSubmit || sending || disabled) return;

    setSending(true);
    try {
      await onSubmit(trimmed);
      setMessage("");
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSubmit();
    }
  }

  const canSubmit = Boolean(message.trim()) && !disabled && !sending;
  const selectedLibraries = libraryScope?.filter((library) => library.selected) ?? [];
  const hasScopeBar = Boolean(showLibraryScopeBar && libraryScope);
  const hasContextBlock = hasScopeBar || attachedDocs.length > 0;

  const contextLabel = hasScopeBar ? "Libraries in scope" : "Attached documents";
  const contextSummary = buildContextSummary(selectedLibraries, attachedDocs, hasScopeBar);

  function openPlusMenu(view: KmComposerPlusMenuView = "root") {
    if (plusMenuOpen && view === "root") {
      setPlusMenuOpen(false);
      setPlusMenuInitialView("root");
      return;
    }
    setPlusMenuInitialView(view);
    setPlusMenuOpen(true);
  }

  return (
    <div className={cn("shrink-0 px-3 pb-4 pt-1 sm:px-5", className)}>
      <div className="relative mx-auto w-full max-w-3xl overflow-visible">
        {scopeLabel && !hasScopeBar ? (
          <div className="mb-2 flex">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px]",
                scopeVariant === "workspace"
                  ? "border-blue-500/25 bg-blue-500/12 text-blue-300"
                  : "border-violet-500/25 bg-violet-500/12 text-violet-300",
              )}
            >
              {scopeVariant === "workspace" ? (
                <Library className="h-3 w-3" />
              ) : (
                <BookOpen className="h-3 w-3" />
              )}
              {scopeLabel}
            </span>
          </div>
        ) : null}

        {hasContextBlock ? (
          <div className="mb-2.5">
            <div className="mb-2 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setIsContextExpanded((prev) => !prev)}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 rounded-md px-1 py-0.5 text-left transition-colors hover:bg-[var(--lm-hover)] hover:text-[#101828] dark:hover:bg-white/5 dark:hover:text-slate-200"
                aria-expanded={isContextExpanded}
                aria-label={isContextExpanded ? "Collapse context" : "Expand context"}
                title={isContextExpanded ? "Collapse scope" : "Expand scope"}
              >
                {isContextExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5 shrink-0 text-[#576274] dark:text-slate-400" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[#576274] dark:text-slate-400" />
                )}
                <span className="shrink-0 text-[11px] uppercase tracking-wide text-[#576274] dark:text-slate-400">
                  {contextLabel}
                </span>
                {!isContextExpanded ? (
                  <span className="truncate text-[11px] normal-case tracking-normal text-[#576274] dark:text-slate-500">
                    · {contextSummary}
                  </span>
                ) : null}
              </button>
              {hasScopeBar ? (
                <IconButton
                  type="button"
                  size="sm"
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    openPlusMenu("libraries");
                  }}
                  title={manageLabel}
                  aria-label={manageLabel}
                >
                  <Settings2 className="h-4 w-4" />
                </IconButton>
              ) : null}
            </div>
            {isContextExpanded ? (
              <>
                {hasScopeBar ? (
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    {selectedLibraries.length > 0 ? (
                      selectedLibraries.map((library) => (
                        <span
                          key={library.id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/45 bg-violet-500/15 px-2.5 py-1 text-[11px] font-medium text-violet-200"
                        >
                          <Check className="h-3 w-3" />
                          {library.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-[11px] text-[#576274] dark:text-slate-500">
                        No libraries selected
                      </span>
                    )}
                  </div>
                ) : null}
                {attachedDocs.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {attachedDocs.map((doc) => {
                      const statusLabel = attachedDocStatusLabel(doc.status);
                      const showSpinner = isAttachedDocProcessing(doc.status);
                      const removable = canRemoveAttachedDoc(doc.status);

                      return (
                        <span
                          key={doc.id}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-blue-500/25 bg-blue-500/12 px-2.5 py-1 text-[11px] text-blue-300"
                        >
                          {showSpinner ? (
                            <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                          ) : (
                            <FileText className="h-3 w-3 shrink-0" />
                          )}
                          <span className="max-w-[200px] truncate">{doc.name}</span>
                          {statusLabel ? (
                            <RagBadge variant={doc.status} className="shrink-0">
                              {statusLabel}
                            </RagBadge>
                          ) : null}
                          {removable ? (
                            <IconButton
                              type="button"
                              size="xs"
                              variant="destructive"
                              onClick={() => onRemoveDoc?.(doc.id)}
                              className="ml-0.5 shrink-0"
                              aria-label={`Remove ${doc.name}`}
                            >
                              <X className="h-3 w-3" />
                            </IconButton>
                          ) : null}
                        </span>
                      );
                    })}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}

        <div
          className={cn(
            "flex w-full flex-col gap-2 overflow-visible border border-[rgba(15,23,42,0.09)] bg-[var(--composer-bg)] px-3 py-2.5 shadow-[var(--shadow-card)] focus-within:border-[rgba(59,130,246,0.45)] focus-within:ring-2 focus-within:ring-[rgba(59,130,246,0.15)] dark:border-white/10 dark:bg-white/5 dark:shadow-[0_8px_28px_rgba(0,0,0,0.35)] dark:focus-within:border-blue-500/50 dark:focus-within:ring-blue-500/20",
            isMultiline ? "rounded-3xl" : "rounded-full",
          )}
        >
          <div
            className={cn(
              "flex min-w-0 gap-2",
              isMultiline ? "items-end" : "items-center",
            )}
          >
            <div className="relative shrink-0 overflow-visible">
              <KmComposerPlusMenu
                open={plusMenuOpen}
                onOpenChange={(next) => {
                  setPlusMenuOpen(next);
                  if (!next) setPlusMenuInitialView("root");
                }}
                initialView={plusMenuInitialView}
                disabled={disabled || sending}
                showLibrariesTab={hasScopeBar}
                libraries={libraryScope}
                onLibraryIdsChange={onLibraryIdsChange}
                onAttachDocs={onAttachDocs}
                agenticAvailable={agenticAvailable}
                agenticEnabled={agenticEnabled}
                onAgenticChange={onAgenticChange}
              />
              <IconButton
                type="button"
                size="sm"
                disabled={disabled || sending}
                active={plusMenuOpen}
                data-composer-plus-trigger=""
                aria-expanded={plusMenuOpen}
                aria-label="Add to chat"
                title="Add to chat"
                onClick={() => openPlusMenu("root")}
              >
                <Plus className="h-4 w-4" />
              </IconButton>
            </div>
            <Textarea
              ref={textareaRef}
              placeholder={placeholder}
              rows={1}
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
                const nextHeight = resizeComposerTextarea(
                  event.target,
                  CHAT_COMPOSER_MIN_HEIGHT_PX,
                  CHAT_COMPOSER_MAX_HEIGHT_PX,
                );
                setTextareaHeight(nextHeight);
              }}
              onKeyDown={handleKeyDown}
              disabled={disabled || sending}
              style={{ minHeight: CHAT_COMPOSER_MIN_HEIGHT_PX }}
              className="max-h-[200px] min-h-[40px] flex-1 resize-none overflow-y-hidden border-0 bg-transparent px-1 py-2 text-sm leading-5 text-[#101828] shadow-none placeholder:text-[#576274] focus-visible:ring-0 dark:border-0 dark:bg-transparent dark:text-white dark:shadow-none dark:placeholder:text-gray-500"
            />
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={!canSubmit}
              aria-label="Send message"
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-full !p-0",
                chatSendButtonClassName(canSubmit),
              )}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ChatComposerScopeChip({
  selectedCount,
}: {
  selectedCount: number;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-blue-500/25 bg-blue-500/12 px-2.5 py-1 text-[11px] text-blue-300">
      <Library className="h-3 w-3" />
      {selectedCount} libraries selected
    </span>
  );
}
